import { Link, useRouterState } from '@tanstack/react-router';
import { Tv, RefreshCw, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { NotificationToggle } from '@/components/NotificationToggle';
import { Footer } from '@/components/Footer';
import { navItems } from '@/lib/nav';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';

export function DesktopShell({ children }: { children: React.ReactNode }) {
  const { location } = useRouterState();
  const qc = useQueryClient();
  const { logout } = useAuth();
  const refresh = async () => {
    await qc.invalidateQueries();
  };

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/90 px-4 backdrop-blur">
        <Link to="/" className="flex items-center gap-2 font-bold tracking-tight">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-black">M</span>
          <span>MAESTRO</span>
        </Link>
        <nav className="ml-6 flex items-center gap-1">
          {navItems.map((it) => {
            const active = location.pathname === it.to;
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                className={cn(
                  'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  active
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{it.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={refresh} aria-label="Rafraîchir">
            <RefreshCw />
          </Button>
          <Button asChild variant="ghost" size="icon" aria-label="Mode TV">
            <a href="?tv=1"><Tv /></a>
          </Button>
          <NotificationToggle />
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={() => logout()} aria-label="Déconnexion" title="Déconnexion">
            <LogOut />
          </Button>
        </div>
      </header>
      <main className="flex-1 px-4 py-6">
        <div className="mx-auto max-w-screen-2xl">{children}</div>
      </main>
      <Footer />
    </div>
  );
}
